// Tokens sémantiques partagés par toute l'ERP — nommés par usage (comme les "dynamic system colors"
// des plateformes Apple), pas par apparence. Le thème MUI global (src/theme/theme.js) est construit à
// partir de ces mêmes valeurs : un composant qui lit `appleTokens` et un composant MUI "brut" parlent
// donc le même langage visuel (couleurs, rayons, ombres).
//
// Nom historique conservé (`appleTokens`) pour ne pas toucher les ~80 imports existants ; `tokens` est
// un alias pour le nouveau code.

export const appleTokens = {
  // Apparence courante ('light' | 'dark', voir `applyColorMode` plus bas et theme/colorMode.js).
  mode: 'light',
  canvas: '#FFFFFF', // fond de page — blanc : les blocs sont délimités par des bordures fines, pas par un fond gris
  surface: '#FFFFFF', // fond de carte / barre de navigation
  surfaceSunken: '#F9FAFB', // fond d'en-tête de tableau / zone secondaire dans une carte
  surfaceHover: '#F3F4F6', // survol d'une ligne / d'un élément neutre
  separator: '#E6E8EC', // bordures fines, séparateurs
  separatorStrong: '#D5D8DE', // bordure de champ de formulaire
  label: '#111318', // texte primaire (et fond des boutons principaux)
  secondaryLabel: '#5B6170', // texte secondaire
  tertiaryLabel: '#9398A3', // texte tertiaire / placeholder
  // Couleur de marque du club (`branding.primary_color`, voir `applyBrandColor` plus bas) — valeurs par défaut
  // ci-dessous (bleu neutre de la plateforme, `DEFAULT_ACCENT`) tant que la config publique n'est pas reçue.
  accent: '#2563EB', // couleur de marque — réservée aux éléments interactifs/actifs
  accentStrong: '#2159D4', // variante plus sombre, lisible en bordure / trait sur fond clair
  accentInk: '#FFFFFF', // texte lisible posé sur `accent` (noir ou blanc selon la luminance)

  // Statuts — une seule signification chacun, réutilisés identiquement dans toutes les cartes.
  status: {
    played: { color: '#16A34A', label: 'Joués' },
    reported: { color: '#E08A00', label: 'Reportés' },
    injured: { color: '#DC2626', label: 'Blessé' },
    noShow: { color: '#3B6FF5', label: 'PVSP' },
    other: { color: '#A1A6B0', label: 'Autres' },
  },

  nextBox: {
    participating: { color: '#16A34A', label: 'Participent' },
    noAnswer: { color: '#E08A00', label: 'Sans réponse' },
    notParticipating: { color: '#A1A6B0', label: 'Ne participent pas' },
  },

  // Barre de navigation latérale (sombre).
  sidebar: {
    bg: '#0F1115',
    border: 'rgba(255,255,255,0.06)',
    text: '#A4A9B4',
    textActive: '#FFFFFF',
    section: '#656B78',
    hover: 'rgba(255,255,255,0.05)',
    active: 'rgba(255,255,255,0.09)',
  },

  // Rayons en px, en chaîne (`borderRadius: t.radius.lg`) : les nombres en `sx` sont multipliés par
  // `theme.shape.borderRadius` (4), ces valeurs-là ne le sont pas.
  radius: { sm: '6px', md: '8px', lg: '12px', xl: '16px', pill: '999px' },

  shadow: {
    card: '0 1px 2px rgba(16,24,40,0.05)', // petit relief d'un élément posé sur une grille / une piste
    raised: '0 4px 16px rgba(16,24,40,0.08)',
    overlay: '0 24px 48px -12px rgba(16,24,40,0.28), 0 0 0 1px rgba(16,24,40,0.04)',
  },

  // UNE seule police pour toute l'app. Les chiffres (scores, montants, compteurs) restent alignés via
  // `fontVariantNumeric: 'tabular-nums'` (voir `type.numeric`) au lieu d'une police monospace.
  fontFamily:
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  // Alias historique : ne pointe plus vers une police monospace, mais vers la police unique.
  monoFont:
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',

  // Échelle typographique UNIQUE — chaque texte de l'app prend l'un de ces rôles (à étaler en `sx` :
  // `sx={{ ...t.type.body, color: t.secondaryLabel }}`). Tailles autorisées : 11/12/13/14/15/16/18/22/28 px,
  // graisses 400/500/600/700. Mêmes valeurs que les variantes MUI du thème (src/theme/theme.js).
  type: {
    display: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.02em' }, // 28 — gros chiffre KPI
    pageTitle: { fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.015em' }, // 22 — titre de page (h1)
    title: { fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.01em' }, // 18 — titre de dialog important, nom d'entité
    sectionTitle: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.35 }, // 16 — titre de dialog / de section
    cardTitle: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.4 }, // 15 — titre de carte
    body: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 }, // 14 — texte courant, champs
    bodyStrong: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 }, // 14 — libellé d'élément de liste
    small: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.45 }, // 13 — texte secondaire, cellules de tableau, boutons
    smallStrong: { fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.45 }, // 13
    caption: { fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.4 }, // 12 — méta, aide, pastilles de statut
    micro: { fontSize: '0.6875rem', fontWeight: 600, lineHeight: 1.35 }, // 11 — texte très dense (planning, matrices), sans capitales
    overline: { fontSize: '0.6875rem', fontWeight: 600, lineHeight: 1.4, letterSpacing: '0.06em', textTransform: 'uppercase' }, // 11 — libellé de groupe
    numeric: { fontVariantNumeric: 'tabular-nums' }, // à combiner avec l'un des rôles ci-dessus
  },
};

export const tokens = appleTokens;

// --- Apparence claire / sombre ---------------------------------------------------------------------------------
// Comme la couleur de marque : les tokens sont MUTÉS en place au démarrage (theme/colorMode.js, importé en premier par
// index.js), avant tout rendu ; changer d'apparence recharge la page (des styles sont calculés au chargement des modules).

const LIGHT_SURFACES = {
  canvas: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceSunken: '#F9FAFB',
  surfaceHover: '#F3F4F6',
  separator: '#E6E8EC',
  separatorStrong: '#D5D8DE',
  label: '#111318',
  secondaryLabel: '#5B6170',
  tertiaryLabel: '#9398A3',
  shadow: {
    card: '0 1px 2px rgba(16,24,40,0.05)',
    raised: '0 4px 16px rgba(16,24,40,0.08)',
    overlay: '0 24px 48px -12px rgba(16,24,40,0.28), 0 0 0 1px rgba(16,24,40,0.04)',
  },
};

const DARK_SURFACES = {
  canvas: '#0E1014',
  surface: '#16181D',
  surfaceSunken: '#1B1E24',
  surfaceHover: '#22262D',
  separator: '#2A2E36',
  separatorStrong: '#3A3F49',
  label: '#ECEEF2',
  secondaryLabel: '#A3A9B6',
  tertiaryLabel: '#6E7480',
  shadow: {
    card: '0 1px 2px rgba(0,0,0,0.4)',
    raised: '0 4px 16px rgba(0,0,0,0.45)',
    overlay: '0 24px 48px -12px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)',
  },
};

/** Applique l'apparence ('light' | 'dark') aux tokens (mutation en place). */
export function applyColorMode(mode) {
  const dark = mode === 'dark';
  Object.assign(appleTokens, dark ? DARK_SURFACES : LIGHT_SURFACES, { mode: dark ? 'dark' : 'light' });
  appleTokens.sidebar = { ...appleTokens.sidebar, bg: dark ? '#0A0B0E' : '#0F1115' };
}

export function tint(hex, alpha) {
  const h = String(hex || '').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  if (!Number.isFinite(n)) return `rgba(142, 142, 147, ${alpha})`;
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// --- Couleur de marque du club ---------------------------------------------------------------------------------
// `appleTokens.accent*` sont MUTÉS en place (de nombreux composants lisent `t.accent` au rendu) ; le thème MUI est
// ensuite reconstruit (`buildTheme()`, src/theme/theme.js) — voir App.js et services/clubPublicConfig.js (cache).

/** Bleu neutre de la plateforme : couleur de marque par défaut (config absente / invalide) ; chaque club a la sienne. */
export const DEFAULT_ACCENT = '#2563EB';

const INK_DARK = '#111318';
const INK_LIGHT = '#FFFFFF';

/** "#rrggbb" normalisé en minuscules (accepte "#rgb" et sans dièse), ou null. */
export function normalizeHexColor(value) {
  const h = String(value || '').trim().replace(/^#/, '');
  const full = /^[0-9a-f]{3}$/i.test(h) ? h.split('').map((c) => c + c).join('') : h;
  return /^[0-9a-f]{6}$/i.test(full) ? `#${full.toLowerCase()}` : null;
}

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(rgb) {
  return `#${rgb.map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('')}`;
}

function luminance([r, g, b]) {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function contrast(l1, l2) {
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

/**
 * Tokens dérivés d'une couleur de marque : `accent` (la couleur), `accentStrong` (assombrie d'au moins 10 %, et
 * davantage si nécessaire pour rester visible en trait sur fond blanc — contraste ≥ 1,75 comme le jaune d'origine),
 * `accentInk` (noir ou blanc, le plus contrasté sur `accent`).
 */
export function brandAccentTokens(color) {
  const accent = normalizeHexColor(color) || DEFAULT_ACCENT.toLowerCase();
  const rgb = hexToRgb(accent);
  const whiteL = 1;
  const darken = (f) => rgb.map((c) => c * f);
  let factor = 0.9;
  while (factor > 0.4 && contrast(luminance(darken(factor)), whiteL) < 1.75) factor -= 0.05;
  const strong = darken(factor);
  const accentL = luminance(rgb);
  const accentInk = contrast(accentL, luminance(hexToRgb(INK_DARK))) >= contrast(accentL, whiteL) ? INK_DARK : INK_LIGHT;
  return { accent: accent.toUpperCase(), accentStrong: rgbToHex(strong).toUpperCase(), accentInk };
}

/**
 * Applique la couleur de marque aux tokens (mutation en place). Couleur vide / invalide = couleur par défaut.
 * Retourne true si les tokens ont changé.
 */
export function applyBrandColor(color) {
  const next = brandAccentTokens(color);
  const changed =
    appleTokens.accent !== next.accent || appleTokens.accentStrong !== next.accentStrong || appleTokens.accentInk !== next.accentInk;
  if (changed) Object.assign(appleTokens, next);
  return changed;
}
